import React, { ReactNode, Fragment } from 'react';
import { ExtendableProps } from '../_internal/components';
import {
  ItemRenderData,
  MinOptionInfo,
  SelectBaseProps,
} from '../_internal/selectBase/types';

export interface SingleSelectBaseProps<
  // `ValueString` makes life easier for consumer when they use an enum
  // for options. This way when `onChange` fires they'll get back an enum
  // value instead of an array of strings
  ValueString extends string = string,
  // `OptionInfo` ensures that we get an object with at least `value`
  // (and optional `disabled`) defined on it
  OptionInfo extends MinOptionInfo<ValueString> = MinOptionInfo<ValueString>,
> extends SelectBaseProps<
    ValueString,
    OptionInfo,
    ItemRenderData<ValueString, 'radio'>
  > {
  /**
   * A handler that is triggered when the component's selected option changes
   */
  onChange: (value: ValueString) => void;
  /**
   * The current selected value of the component
   */
  value?: ValueString;
}

/**
 * Allows for the props of a parent component to extend
 * the props of `SingleSelectBase` *before* they are merged with the
 * props of the underlying element
 */
export type ExtendableSingleSelectBaseProps<
  ValueString extends string,
  OptionInfo extends MinOptionInfo<ValueString>,
  OverrideProps = {},
> = ExtendableProps<
  SingleSelectBaseProps<ValueString, OptionInfo>,
  OverrideProps
>;

// The default container does nothing (returns back the contents)
const DEFAULT_CONTAINER = (children: ReactNode) => children;

/**
 * `SingleSelectBase` is a headless / display-free base component. Use it
 * as a building block for creating components that allow for one single
 * selected option from a set of options. It manages the selected value state
 * and core accessibility, while offloading visual display of the selectable
 * UI to the user via the `children` render prop. Example UIs: radio button
 * group, tabs, etc.
 */
const SingleSelectBase = <
  ValueString extends string,
  OptionInfo extends MinOptionInfo<ValueString>,
>({
  name,
  idPrefix = name,
  value,
  options,
  children,
  container = DEFAULT_CONTAINER,
  onChange,
  disabled = false,
}: SingleSelectBaseProps<ValueString, OptionInfo>) => {
  const renderedOptions = options.map((currentOption, index) => {
    const { value: optionValue, disabled: optionDisabled = false } =
      currentOption;
    const renderedOption = children(
      {
        value: optionValue,
        id: `${idPrefix}-${optionValue}`,
        name,
        like: 'radio',
        isSelected: optionValue === value,
        disabled: disabled || optionDisabled,
        onChange: () => {
          // act like a radio by only selecting when clicked
          onChange(optionValue);
        },
      },
      // passing through the option they passed so that they can read
      // any additional properties they added (like `label`) needed
      // for rendering
      currentOption,
      index,
    );

    // The fragment is needed here in order to add a `key` prop
    // for each option
    return <Fragment key={optionValue}>{renderedOption}</Fragment>;
  });

  // the need for the fragment is because the container returns a
  // `React.Node` and it can be multiple elements. We need the return
  // value from the component to be a single parent element.
  return <>{container(renderedOptions)}</>;
};

export default SingleSelectBase;
